import { useEffect, useRef, useState } from "react";

import { Controller, FormProvider, useForm } from "react-hook-form";

import { useMutation } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { EyeIcon, EyeOffIcon, SparklesIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";

import DemoAccountPanel, { DemoAccount } from "@/features/auth/components/DemoAccountsPanel";
import { LoginSchema, loginSchema } from "@/features/auth/schemas";
import Error from "@/shared/components/Error";
import { useAuthentication } from "@/shared/hooks/useAuthentication";
import { login } from "@/features/auth/services/auth";
import { cn } from "@/shared/lib/utils/cn";
import { AppError } from "@/shared/lib/utils/errors";

export default function LoginForm() {
  const { authenticate } = useAuthentication();

  const [showPassword, setShowPassword] = useState(false);
  const [demoAccountSelected, setDemoAccountSelected] = useState<DemoAccount | null>(null);
  const [submitPulse, setSubmitPulse] = useState(false);

  const submitRef = useRef<HTMLButtonElement>(null);

  const form = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      emailAddress: "",
      password: "",
    },
  });

  const watchedEmail = form.watch("emailAddress");
  const watchedPassword = form.watch("password");

  const mutation = useMutation<string, AppError | Error, LoginSchema>({
    mutationFn: login,
    onSuccess: (session) => {
      authenticate(session);
      window.location.href = "/";
    },
  });

  async function onSubmit(values: LoginSchema) {
    mutation.mutate(values);
  }

  function handleDemoSelect(account: DemoAccount) {
    setDemoAccountSelected(account);

    form.setValue("emailAddress", account.email, { shouldValidate: true, shouldDirty: true });
    form.setValue("password", account.password, { shouldValidate: true, shouldDirty: true });

    setTimeout(() => {
      submitRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setSubmitPulse(true);
      setTimeout(() => setSubmitPulse(false), 600);
    }, 50);
  }

  useEffect(() => {
    if (!demoAccountSelected) {
      return;
    }

    const hasEmailChanged = watchedEmail !== demoAccountSelected.email;
    const hasPasswordChanged = watchedPassword !== demoAccountSelected.password;

    if (hasEmailChanged || hasPasswordChanged) {
      setDemoAccountSelected(null);
    }
  }, [watchedEmail, watchedPassword, demoAccountSelected]);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup>
          {mutation.isError && <Error title="Unable to sign you in" error={mutation.error} />}
          <Controller
            name="emailAddress"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={field.name}>Email address</FieldLabel>
                <Input {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} placeholder="john.smith@example.com" autoComplete="email"></Input>
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          ></Controller>
          <Controller
            name="password"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={field.name}>Password</FieldLabel>
                <div className="relative">
                  <Input {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} placeholder="••••••••" autoComplete="off" type={showPassword ? "text" : "password"} className="pr-10"></Input>
                  <button type="button" onClick={() => setShowPassword((p) => !p)} aria-label={showPassword ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors">
                    {showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
                  </button>
                </div>
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          ></Controller>
          <Button
            type="submit"
            ref={submitRef}
            className={cn("w-full mt-1 transition-all duration-300", submitPulse ? "scale-[1.03] brightness-110" : "", !!demoAccountSelected ? "ring-2 ring-primary/40 ring-offset-2 ring-offset-card" : "")}
            disabled={mutation.isPending || mutation.isSuccess}
          >
            {mutation.isPending || mutation.isSuccess ? (
              "Signing in..."
            ) : !!demoAccountSelected ? (
              <>
                <SparklesIcon className="size-4" />
                Sign in as demo account
              </>
            ) : (
              "Sign in"
            )}
          </Button>
        </FieldGroup>
      </form>
      <Separator />
      <DemoAccountPanel account={demoAccountSelected} onSelect={handleDemoSelect} />
    </FormProvider>
  );
}
